import React from 'react';
import { Box, Typography } from '@material-ui/core';
import WarningAmberIcon from '@material-ui/icons/Warning';
import { RMSCommonDialog } from '../../../shared/components/dialog';
import RMSChip from '../../../shared/components/chips/RMSChip';
import { formatDateTime } from '../../../shared/utility/dateFormatter';
import { normalizeStatus, getStatusChipClass } from '../../constants/evidenceGridColumns';
import {
  PORTAL_ITEM_DETAIL_FIELDS,
  PORTAL_ITEM_DISPOSITION_FIELDS,
} from '../../constants/evidencePortalConstants';

const toSingle = (value) => (Array.isArray(value) ? value[0] : value);
const toList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

// type: true = date, 'bool' = boolean shown only when true (e.g. "Disposed: Yes"), else plain text
const renderDetailRows = (source, fields) =>
  fields
    .filter(([, key, type]) => {
      const value = source?.[key];
      if (type === 'bool') return value === true || value === 1;
      return value !== null && value !== undefined && value !== '';
    })
    .map(([label, key, type]) => (
      <div key={key} className="evidence-portal-detail__row">
        <span className="evidence-portal-detail__label">{label}</span>
        <span className="evidence-portal-detail__value">
          {type === 'bool'
            ? 'Yes'
            : type === true
            ? formatDateTime(source[key])
            : String(source[key])}
        </span>
      </div>
    ));

const EvidencePortalItemDetailDialog = ({ open, loading, data, matchedRules, onClose }) => {
  const statusLabel = normalizeStatus(data?.Status) || 'Waiting';
  const disposition = toSingle(data?.EvidenceDisposition);
  const custody = toList(data?.EvidenceCustody);
  const storage = toSingle(data?.EvidenceStorage);

  return (
    <RMSCommonDialog
      open={open}
      onClose={onClose}
      loading={loading}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{ show: false }}
      cancelButton={{ show: true, type: 'simple', variant: 'outlined', label: 'Close' }}
      title={
        data && (
          <Box>
            <Typography variant="h6" className="evidence-portal-detail__eid">
              {data.EvidenceID}
            </Typography>
            <Typography variant="body2" className="evidence-portal-detail__iId">
              Incident: {data.IncidentNumber || data.IncidentID || '—'}
              {data.AgencyID ? ` • Agency: ${data.AgencyID}` : ''}
            </Typography>
          </Box>
        )
      }>
      {data && (
        <div className="evidence-portal-detail">
          <div className="evidence-portal-detail__pills">
            <RMSChip
              label={statusLabel}
              size="small"
              className={`evidence-chip evidence-chip--status ${getStatusChipClass(statusLabel)}`}
            />
            {data.CurrentLocation && (
              <RMSChip
                label={data.CurrentLocation}
                size="small"
                className="evidence-portal-detail__pill"
              />
            )}
            {storage?.StorageLocation && (
              <RMSChip
                label={`${storage.StorageLocation}${
                  storage.SubLocation ? ' / ' + storage.SubLocation : ''
                }`}
                size="small"
                className="evidence-portal-detail__pill"
              />
            )}
          </div>

          {matchedRules?.length > 0 && (
            <div className="evidence-portal-detail__warning">
              <div className="evidence-portal-detail__warning-title">
                <WarningAmberIcon fontSize="small" /> Disposal Rule Match
              </div>
              {matchedRules.map((rule) => (
                <div key={rule.name} className="evidence-portal-detail__warning-row">
                  <span
                    className="evidence-portal-detail__warning-dot"
                    style={{ background: rule.color || '#dc2626' }}
                  />
                  {rule.name}
                </div>
              ))}
            </div>
          )}

          <Typography variant="overline" className="evidence-portal-detail__section-title">
            Item Details
          </Typography>
          <div className="evidence-portal-detail__grid">
            {renderDetailRows(data, PORTAL_ITEM_DETAIL_FIELDS)}
          </div>

          {disposition && (
            <>
              <Typography variant="overline" className="evidence-portal-detail__section-title">
                Disposition
              </Typography>
              <div className="evidence-portal-detail__grid">
                {renderDetailRows(disposition, PORTAL_ITEM_DISPOSITION_FIELDS)}
              </div>
            </>
          )}

          {custody.length > 0 && (
            <>
              <Typography variant="overline" className="evidence-portal-detail__section-title">
                Chain of Custody ({custody.length})
              </Typography>
              <div className="evidence-portal-detail__custody">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Reason</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custody.map((item, index) => (
                      <tr key={item.ptsEvidenceCustodyID || index}>
                        <td>{formatDateTime(item.TransactionDate)}</td>
                        <td>{item.TransactionType}</td>
                        <td>{item.CustodyFrom}</td>
                        <td>{item.CustodyTo}</td>
                        <td>{item.Reason}</td>
                        <td>{item.Notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </RMSCommonDialog>
  );
};

export default EvidencePortalItemDetailDialog;
